import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ai } from '@repletoryreach/sdk-api';
import { Card, Stack, Text, Title, Button, Group } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Select, Switch, TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../i18n/I18nProvider';
import { AiError, ModelSelect, UnitCost, useModelsOf } from './shared';

const SIZES = ['square', 'landscape', 'portrait'] as const;

/**
 * Pictures (14 October 2026): pictures made from a description, charged per picture, to download or keep in the
 * media library. The model may run outside South Africa, and the page says so before anything is sent.
 */
export function PicturesTab() {
  const t = useT();
  const models = useModelsOf('image');
  const [model, setModel] = useState('');
  const chosen = models.find((m) => m.code === model) ?? models[0];
  const [prompt, setPrompt] = useState('');
  const [avoid, setAvoid] = useState('');
  const [size, setSize] = useState<(typeof SIZES)[number]>('square');
  const [count, setCount] = useState('1');
  const [save, setSave] = useState(false);

  const run = useMutation({
    mutationFn: () => ai.images({
      prompt: prompt.trim(), avoid: avoid.trim() || null, size, count: Number(count), model: chosen?.code ?? null, save
    })
  });
  const result = run.data?.data;
  const ready = !!chosen && !!prompt.trim();

  return (
    <Stack gap="lg">
      <Card withBorder>
        <form onSubmit={(e) => { e.preventDefault(); if (ready) run.mutate(); }}>
          <Stack gap="md">
            <Title order={4}>{t('ai.pictures.title')}</Title>
            <Textarea label={t('ai.pictures.prompt')} description={t('ai.pictures.promptHint')} value={prompt}
              onChange={(e) => setPrompt(e.currentTarget.value)} maxLength={1024} minRows={3} autosize required data-testid="ai-pictures-prompt" />
            <TextInput label={t('ai.pictures.avoid')} description={t('ai.pictures.avoidHint')} value={avoid}
              onChange={(e) => setAvoid(e.currentTarget.value)} maxLength={1024} />
            <Group grow align="flex-start">
              <Select label={t('ai.pictures.size')} value={size} onChange={(e) => setSize(e.currentTarget.value as (typeof SIZES)[number])}
                data={SIZES.map((s) => ({ value: s, label: t(`ai.pictures.size.${s}`) }))} />
              <Select label={t('ai.pictures.count')} value={count} onChange={(e) => setCount(e.currentTarget.value)}
                data={['1', '2', '3', '4'].map((n) => ({ value: n, label: n }))} />
            </Group>
            <ModelSelect kind="image" value={chosen?.code ?? ''} onChange={setModel} />
            {chosen?.outside_south_africa && <Alert color="warning">{t('ai.pictures.outsideSa')}</Alert>}
            <Switch label={t('ai.unit.save')} description={t('ai.unit.saveHint')} checked={save} onChange={setSave} />
            <Group justify="flex-end">
              <Button type="submit" loading={run.isPending} disabled={!ready} data-testid="ai-pictures-run">{t('ai.pictures.run')}</Button>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('ai.chargeNote')}</Text>
          </Stack>
        </form>
      </Card>
      <AiError error={run.error} />
      {result && (
        <Card withBorder>
          <Stack gap="sm" data-testid="ai-result">
            {result.stub && <Text role="bodySmall">{t('ai.result.stub')}</Text>}
            <div className="rr-ai__pictures">
              {result.images.map((p, i) => {
                const src = p.data ? `data:${p.media_type};base64,${p.data}` : p.url ?? '';
                return (
                  <figure key={i} className="rr-ai__picture">
                    <img src={src} alt={t('ai.pictures.alt', { number: i + 1, prompt: prompt.trim() })} />
                    <figcaption>
                      <a href={src} download={`picture-${i + 1}.png`} target={p.data ? undefined : '_blank'} rel="noreferrer">
                        {p.url ? t('ai.unit.open') : t('ai.unit.download')}
                      </a>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
            <UnitCost minor={result.charged_minor} saved={result.images.some((p) => !!p.media_id)} />
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
